import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CreditClaimStatus } from '../../constants/ApplicationStatus';
import { ModalType } from '../../constants/ModalType';
import { buildRoute, Routes } from '../../constants/Routes';
import { updateClaim } from '../../ducks/claims';
import { hideModal, setModalPayload, showModal } from '../../ducks/modal';
import { isAdmin as isAdminSelector, isCreditProcessor as isCreditProcessorSelector } from '../../selectors/user';
import { CreditClaim } from '../../types';
import ActionMenu, { ActionMenuItem } from '../ActionMenu';
import NotesModal from './NotesModal';
import PaymentFailedModal from './PaymentFailedModal';
import PRARestoreFailedModal from './PRARestoreFailedModal';

interface Props {
  claim: CreditClaim;
  history?: any;
}

const ClaimActionMenu: React.FunctionComponent<Props> = ({ claim, history }) => {
  const dispatch = useDispatch();
  const isAdmin = useSelector(isAdminSelector);
  const isCreditProcessor = useSelector(isCreditProcessorSelector);

  const goToUserProfile = () => {
    if (claim.creditEarner) {
      history.push(buildRoute(Routes.UserShow, { id: claim.creditEarner.id }));
    }
  };

  const showPaymentFailedModal = () => {
    dispatch(showModal(`${ModalType.PaymentFailed}-${claim.id}`));
  };

  const showNotesModal = () => {
    dispatch(showModal(`${ModalType.ApplicationNotes}-${claim.id}`));
  };

  const updateClaimStatus = (checked: boolean) => {
    if (checked) {
      dispatch(
        updateClaim(
          claim.id,
          { status: CreditClaimStatus.PAYMENT_FAILED },
          {
            onSuccess: () => {
              dispatch(hideModal(`${ModalType.PaymentFailed}-${claim.id}`));
            },
          }
        )
      );
    }
  };

  const updateClaimDeletionStatus = (newDeleted: boolean) => {
    dispatch(
      updateClaim(
        claim.id,
        { deleted: newDeleted },
        {
          onSuccess: () => {
            dispatch(hideModal(`${ModalType.PaymentFailed}-${claim.id}`));
          },
          onError: (error) => {
            const message =
              error?.message ||
              'There is already an active PRA application for this user. Please resolve the existing application before restoring this one.';
            const modalId = `${ModalType.PRARestoreFailed}-${claim.id}`;
            dispatch(setModalPayload({ id: modalId, payload: message }));
            dispatch(showModal(modalId));
          },
        }
      )
    );
  };

  const softDeleteClaimStatus = () => {
    updateClaimDeletionStatus(true);
  };
  const restoreClaimFromSoftDeletion = () => {
    updateClaimDeletionStatus(false);
  };

  return (
    <>
      <ActionMenu>
        <ActionMenuItem onClick={goToUserProfile}>Applicant Profile</ActionMenuItem>
        {claim.status === CreditClaimStatus.COMPLETED && (
          <ActionMenuItem onClick={showPaymentFailedModal}>Mark as Payment Failed</ActionMenuItem>
        )}
        <ActionMenuItem onClick={showNotesModal}>View Notes</ActionMenuItem>
        {(isAdmin || isCreditProcessor) &&
          (claim.deleted ? (
            <ActionMenuItem onClick={restoreClaimFromSoftDeletion}>Restore</ActionMenuItem>
          ) : (
            <ActionMenuItem disabled={claim.status === CreditClaimStatus.COMPLETED} onClick={softDeleteClaimStatus}>
              Delete
            </ActionMenuItem>
          ))}
      </ActionMenu>

      {claim.status === CreditClaimStatus.COMPLETED && <PaymentFailedModal claim={claim} handleConfirm={updateClaimStatus} />}
      {claim.deleted && <PRARestoreFailedModal claim={claim} />}
      <NotesModal claim={claim} />
    </>
  );
};

export default ClaimActionMenu;
